import React, { memo, useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dropdown, DropdownProps } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { CustomTreeNode, EntitiesTreeSearch } from 'features/EntitiesTreeSearch';
import { accountsWithProjectsByDateService } from 'entities/account';
import { timesheetsStore } from 'entities/timesheet';
import { ReactComponent as OpenTreeSelectIcon } from 'shared/assets/icons/dynamicColor/settingsIcon.svg';
import { useDebounceValue } from 'shared/lib/debounce';
import { getKeysFromAccounts, mergeWithGrayedOut } from '../../lib/accountTreeUtils';
import { useGrayedOutAccounts } from '../../lib/useGrayedOutAccounts';

import styles from './AccountAndProjectTreeSelect.module.scss';

const AccountAndProjectTreeSelect = ({ disabled }: { disabled: boolean }): React.ReactElement => {
  const { t } = useTranslation('Timesheet');

  const setTopLevelFilters = useStore(timesheetsStore, (state) => state.setTopLevelFilters);
  const range = useStore(timesheetsStore, (state) => state.selectedRange);

  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<string[]>(['all']);

  const debouncedSearchValue = useDebounceValue(searchValue, 500);
  const isSearching = debouncedSearchValue.length > 0;

  const { data: accountsAndProjects = [], isFetching } = useQuery(
    accountsWithProjectsByDateService.queryOptions({
      startDate: range[0].format('YYYY-MM-DD'),
      endDate: range[1].format('YYYY-MM-DD'),
      hideNonActive: !showInactive,
      query: debouncedSearchValue,
    }),
  );

  const { grayedOutAccounts, fullAvailableKeys, handleRemoveGrayedOut } = useGrayedOutAccounts({
    accountsAndProjects,
    checkedKeys,
    isSearching,
  });

  const apiKeys = useMemo(() => getKeysFromAccounts(accountsAndProjects), [accountsAndProjects]);

  const displayAccounts = useMemo(
    () => mergeWithGrayedOut(accountsAndProjects, grayedOutAccounts, checkedKeys, isSearching),
    [accountsAndProjects, grayedOutAccounts, checkedKeys, isSearching],
  );

  const treeData: CustomTreeNode[] = useMemo(
    () => [
      {
        title: t('accountAndProjectTreeSelect.label'),
        key: 'all',
        isLeaf: true,
        children: displayAccounts
          ?.sort((a, b) => {
            const aInApi = apiKeys.has(a.id);
            const bInApi = apiKeys.has(b.id);
            if (aInApi !== bInApi) return bInApi ? 1 : -1;
            return Number(b.isActive) - Number(a.isActive);
          })
          .map((item) => {
            const isInApi = apiKeys.has(item.id);
            return {
              title: item.name,
              key: item.id,
              isActive: isInApi && item.isActive,
              className: isInApi && item.isActive ? '' : 'inactiveNode',
              children: item.projects
                ?.sort((a, b) => {
                  const aInApi = apiKeys.has(a.id);
                  const bInApi = apiKeys.has(b.id);
                  if (aInApi !== bInApi) return bInApi ? 1 : -1;
                  return Number(b.isActive) - Number(a.isActive);
                })
                .map((project) => {
                  const projectInApi = apiKeys.has(project.id);
                  return {
                    title: project.name,
                    key: project.id,
                    isActive: projectInApi && project.isActive,
                    className: projectInApi && project.isActive ? '' : 'inactiveNode',
                  };
                }),
            };
          }),
      },
    ],
    [displayAccounts, t, apiKeys],
  );

  const projectKeys = useMemo(
    () => displayAccounts?.flatMap((account) => account.projects?.map((project) => project.id) || []),
    [displayAccounts],
  );

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  };

  const handleOk = useCallback(() => {
    if (checkedKeys.at(0) === 'all') {
      setTopLevelFilters({ projectIds: projectKeys });
      setOpen(false);
      return;
    }
    const checkedProjectKeys = checkedKeys.filter((key) => projectKeys.includes(key));
    setTopLevelFilters({ projectIds: checkedProjectKeys });
    setOpen(false);
  }, [checkedKeys, projectKeys, setTopLevelFilters]);

  const renderPopupDropdown = useCallback(
    () => (
      <EntitiesTreeSearch
        treeData={treeData}
        isLoading={isFetching}
        handleOk={handleOk}
        checkedKeys={checkedKeys}
        setCheckedKeys={setCheckedKeys}
        removeOldKey={handleRemoveGrayedOut}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        showInactive={showInactive}
        setShowInactive={setShowInactive}
        allAvailableKeys={fullAvailableKeys}
      />
    ),
    [treeData, isFetching, handleOk, checkedKeys, handleRemoveGrayedOut, searchValue, showInactive, fullAvailableKeys],
  );

  return (
    <Dropdown
      popupRender={renderPopupDropdown}
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName={styles.dropdownContainer}
      destroyOnHidden
      disabled={disabled}
    >
      <Button
        className={classNames(styles.dropdownBtn, { [styles.activeDropdownBtn]: open })}
        icon={<OpenTreeSelectIcon className={styles.icon} />}
      >
        {t('employeeSelect.dropdownBtn.text')}
      </Button>
    </Dropdown>
  );
};

export default memo(AccountAndProjectTreeSelect);
